import type { Currency } from "@loot-os/api-contract";
import { useId } from "react";
import { EmptyState } from "../../../../core/EmptyState.js";
import { LoadMore } from "../../../../core/LoadMore.js";
import { Text } from "../../../../core/Text.js";
import { BoxCard } from "../../../../domain/BoxCard.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import { BoxCardsSkeleton } from "../../../shared/BoxCards.js";
import { LoadError } from "../../../shared/LoadError.js";

import type { ForgeCardContent, ForgeGridContent } from "../../forgeContent.js";

const GRID = "grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5";

/**
 * The boxes shoppers built, as the catalog's cards with who built each, or the grid's loading,
 * failed or empty state in their place. A failure keeps what already loaded.
 */
export function ForgeGrid({ title, currency, state, more }: ForgeGridContent) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      aria-busy={state.status === "loading" || undefined}
      className="flex flex-col gap-4"
    >
      <Text as="h2" id={id} className="sr-only">
        {title}
      </Text>
      {state.status === "loading" && <BoxCardsSkeleton columns="grid" count={10} />}
      {state.status === "error" && <LoadError message={state.message} />}
      {state.status === "empty" && (
        <EmptyState
          title={state.message}
          action={state.action && <ActionButton action={state.action} variant="secondary" />}
        />
      )}
      {state.status === "ready" && (
        <ul className={GRID}>
          {state.cards.map((card) => (
            <li key={card.box.id}>
              <ForgeCard card={card} currency={currency} />
            </li>
          ))}
        </ul>
      )}
      {more && (
        <LoadMore
          shown={more.shown}
          hasMore
          autoLoad
          loading={more.loading}
          label={more.label}
          onLoadMore={more.onLoadMore}
        />
      )}
    </section>
  );
}

/** A shopper-built box as the catalog's card, with who built it: the Forge's grid and Home's showcase. */
export function ForgeCard({
  card: { box, href, byline, topCard, volatility, volatilityWords },
  currency,
}: {
  card: ForgeCardContent;
  currency: Currency;
}) {
  return (
    <BoxCard
      name={box.name}
      image={box.imageUrl}
      price={box.priceCents}
      href={href}
      byline={byline}
      topCard={topCard}
      volatility={volatility}
      volatilityWords={volatilityWords}
      currency={currency}
    />
  );
}
